import { useState, useEffect, useRef } from "react";
import { Switch, Select, Modal } from "antd";
import {
  IoWalletOutline, IoFlashOutline, IoAddCircleOutline,
  IoCardOutline, IoCheckmarkCircleOutline,
} from "react-icons/io5";
import api from "../../../core/axios/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../core/data/redux/slices/ToastSlice";
import { fetchPaymentMethods, attachPaymentMethod } from "../../../core/data/redux/slices/BillingSlice";
import { fetchCredits, setBalance, setAutoRecharge } from "../../../core/data/redux/slices/CreditsSlice";
import { stripePromise } from "../../../core/config/stripeConfig";

const CREDIT_OPTIONS  = [10, 25, 50, 100, 200, 500];
const THRESHOLD_OPTIONS = [2, 5, 10, 20, 50];
const RECHARGE_OPTIONS  = [25, 50, 100, 200, 500];

const CardBrandIcon = ({ brand }) => {
  const map = { visa: "💳", mastercard: "💳", amex: "💳" };
  return <span style={{ fontSize: 18 }}>{map[brand] || "💳"}</span>;
};

export default function Credits() {
  const dispatch = useDispatch();
  const { paymentMethods: rawPMs } = useSelector((s) => s.billing);
  const paymentMethods = rawPMs || [];

  // Balance / auto-recharge from Redux
  const balance    = useSelector((s) => s.credits.balance);
  const loading    = useSelector((s) => s.credits.loading);
  const arRedux    = useSelector((s) => s.credits.autoRecharge);

  const [addAmount, setAddAmount]   = useState(100);
  const [autoEnabled, setAutoEnabled]   = useState(arRedux.enabled ?? false);
  const [threshold, setThreshold]       = useState(arRedux.threshold ?? 5);
  const [rechargeAmt, setRechargeAmt]   = useState(arRedux.amount ?? 100);
  const [autoSaving, setAutoSaving]     = useState(false);

  // Payment modal state
  const [payModal, setPayModal]             = useState(false);
  const [selectedPM, setSelectedPM]         = useState(null);
  const [cardMode, setCardMode]             = useState("existing"); // "existing" | "new"
  const [cardError, setCardError]           = useState(null);
  const [submitting, setSubmitting]         = useState(false);

  // Stripe elements for new card
  const [stripe, setStripe]         = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const cardRef = useRef(null);

  // ── Init Stripe ────────────────────────────────────────────────────────────
  useEffect(() => {
    stripePromise.then((s) => { if (s) setStripe(s); });
    dispatch(fetchPaymentMethods());
  }, [dispatch]);

  useEffect(() => {
    if (paymentMethods.length > 0 && !selectedPM) {
      setSelectedPM(paymentMethods[0].id);
    }
    if (paymentMethods.length === 0) setCardMode("new");
  }, [paymentMethods]);

  // Mount card element when modal opens in "new" mode
  useEffect(() => {
    if (!payModal || cardMode !== "new" || !stripe || !cardRef.current) return;
    const elements = stripe.elements();
    const el = elements.create("card", {
      hidePostalCode: true,
      style: { base: { fontSize: "15px", color: "#111827", "::placeholder": { color: "#9ca3af" } } },
    });
    el.mount(cardRef.current);
    setCardElement(el);
    return () => { try { el.destroy(); } catch (_) {} setCardElement(null); };
  }, [payModal, cardMode, stripe]);

  // ── Fetch data via Redux ───────────────────────────────────────────────────
  useEffect(() => { dispatch(fetchCredits()); }, [dispatch]);

  // Sync form fields when Redux autoRecharge loads / changes
  useEffect(() => {
    setAutoEnabled(arRedux.enabled === true);
    setThreshold(arRedux.threshold ?? 5);
    setRechargeAmt(arRedux.amount ?? 100);
    console.log(`[Auto-recharge] Loaded from Redux — enabled: ${arRedux.enabled} | threshold: $${arRedux.threshold} | recharge: $${arRedux.amount}`);
  }, [arRedux]);

  // ── Pay with card ──────────────────────────────────────────────────────────
  const handlePay = async () => {
    setSubmitting(true);
    setCardError(null);
    try {
      let pmId = selectedPM;

      // If new card — tokenise and attach
      if (cardMode === "new") {
        if (!cardElement || !stripe) {
          setCardError("Card form is not ready. Please wait a moment.");
          setSubmitting(false);
          return;
        }
        const { paymentMethod, error } = await stripe.createPaymentMethod({ type: "card", card: cardElement });
        if (error) { setCardError(error.message); setSubmitting(false); return; }
        await dispatch(attachPaymentMethod({
          paymentMethodId: paymentMethod.id,
          setAsDefault: paymentMethods.length === 0,
        })).unwrap();
        pmId = paymentMethod.id;
      }

      if (!pmId) { setCardError("Please select or add a card."); setSubmitting(false); return; }

      const res = await api.post("/billing/credits/purchase", {
        amount: addAmount,
        paymentMethodId: pmId,
      });

      dispatch(setBalance(res.data.creditBalance));
      setPayModal(false);
      dispatch(showToast({ message: `$${addAmount} added to your balance!`, variant: "success" }));
    } catch (err) {
      const msg = err?.response?.data?.message || "Payment failed. Please try again.";
      setCardError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Auto-recharge save ─────────────────────────────────────────────────────
  const handleSaveAuto = async () => {
    setAutoSaving(true);
    try {
      await api.put("/billing/credits/auto-recharge", { enabled: autoEnabled, threshold, amount: rechargeAmt });
      dispatch(setAutoRecharge({ enabled: autoEnabled, threshold, amount: rechargeAmt }));
      console.log(`[Auto-recharge] Saved — enabled: ${autoEnabled} | threshold: $${threshold} | recharge: $${rechargeAmt}`);
      dispatch(showToast({ message: `Auto-recharge ${autoEnabled ? "enabled" : "disabled"} and saved`, variant: "success" }));
    } catch (_) {
      dispatch(showToast({ message: "Failed to save settings", variant: "danger" }));
    } finally { setAutoSaving(false); }
  };


  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="card mb-3">
          <div className="card-body pb-2">
            <h4 className="page-title mb-0">Credits</h4>
            <p style={{ color: "#9ca3af", fontSize: 13, marginTop: 4, marginBottom: 0 }}>
              Manage your call credits balance and auto-recharge settings.
            </p>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>

          {/* ── Balance card ── */}
          <div style={{ borderRadius: 16, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #f3f4f6" }}>
            {/* Gradient top banner */}
            <div style={{
              background: "linear-gradient(135deg, #6b46c1 0%, #818cf8 100%)",
              padding: "16px 20px",
              display: "flex", alignItems: "center", gap: 14,
              borderRadius: "15px 15px 0 0",
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <IoWalletOutline size={19} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.65)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 1 }}>Available Balance</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: "#fff", lineHeight: 1 }}>
                  {loading ? "—" : `$${balance.toFixed(2)}`}
                </div>
              </div>
            </div>

            {/* White bottom panel */}
            <div style={{ background: "#fff", padding: "22px 28px 26px" }}>
              <label style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, display: "block", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Top Up Amount
              </label>

              {/* Dropdown */}
              <Select
                size="large"
                value={addAmount}
                onChange={setAddAmount}
                style={{ width: "100%", marginBottom: 14 }}
                dropdownStyle={{ borderRadius: 12 }}
                optionLabelProp="label"
              >
                {CREDIT_OPTIONS.map((v) => (
                  <Select.Option key={v} value={v} label={`$${v}.00`}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: 600 }}>${v}.00</span>
                      <span style={{ fontSize: 12, color: "#9ca3af" }}>Add ${v} credits</span>
                    </div>
                  </Select.Option>
                ))}
              </Select>

              <button
                onClick={() => { setPayModal(true); setCardError(null); }}
                style={{
                  width: "100%", padding: "13px 0",
                  background: "linear-gradient(135deg, #6b46c1 0%, #818cf8 100%)",
                  color: "#fff", border: "none", borderRadius: 12,
                  fontWeight: 700, fontSize: 15, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  boxShadow: "0 4px 14px rgba(107,70,193,0.3)",
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
              >
                <IoAddCircleOutline size={20} />
                Add ${addAmount}.00 — Pay with Card
              </button>
            </div>
          </div>

          {/* ── Auto-recharge card ── */}
          <div style={{ background: "#fff", borderRadius: 16, padding: "28px 28px 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #f3f4f6" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg,#f59e0b,#fbbf24)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <IoFlashOutline size={26} color="#fff" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Auto Recharge</div>
                <div style={{ fontSize: 12, color: "#9ca3af" }}>Avoid missing calls when balance runs low</div>
              </div>
              <Switch
                checked={autoEnabled}
                onChange={setAutoEnabled}
                style={{ background: autoEnabled ? "#3C2371" : undefined }}
              />
            </div>

            <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 18, marginTop: 14, opacity: autoEnabled ? 1 : 0.45, transition: "opacity 0.2s" }}>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 10 }}>
                Opt for auto recharge to avoid missing out on calls when credits are over.
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: "#374151", fontWeight: 500, whiteSpace: "nowrap" }}>When balance falls below</span>
                <Select value={threshold} onChange={setThreshold} disabled={!autoEnabled} style={{ width: 90 }}>
                  {THRESHOLD_OPTIONS.map((v) => <Select.Option key={v} value={v}>${v}</Select.Option>)}
                </Select>
                <span style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>recharge</span>
                <Select value={rechargeAmt} onChange={setRechargeAmt} disabled={!autoEnabled} style={{ width: 100 }}>
                  {RECHARGE_OPTIONS.map((v) => <Select.Option key={v} value={v}>${v}</Select.Option>)}
                </Select>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <button
                onClick={handleSaveAuto}
                disabled={autoSaving}
                style={{
                  padding: "9px 24px", background: "#f59e0b", color: "#fff",
                  border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13,
                  cursor: autoSaving ? "not-allowed" : "pointer",
                  opacity: autoSaving ? 0.6 : 1,
                }}
              >
                {autoSaving ? "Saving…" : "Save Settings"}
              </button>
            </div>
          </div>
        </div>

        {/* ── Payment modal ── */}
        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <IoCardOutline size={20} color="#3C2371" />
              <span>Add ${addAmount} Credits</span>
            </div>
          }
          open={payModal}
          onCancel={() => { setPayModal(false); setCardError(null); }}
          onOk={handlePay}
          okText={submitting ? "Processing…" : `Pay $${addAmount}`}
          okButtonProps={{
            loading: submitting,
            style: { background: "#3C2371", borderColor: "#3C2371" },
          }}
          width={480}
        >
          <div style={{ paddingTop: 8 }}>
            {/* Saved cards */}
            {paymentMethods.length > 0 && (
              <>
                <label style={{ fontWeight: 600, fontSize: 13, display: "block", marginBottom: 10 }}>Select payment method</label>
                {paymentMethods.map((pm) => (
                  <div
                    key={pm.id}
                    onClick={() => { setSelectedPM(pm.id); setCardMode("existing"); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 14px", borderRadius: 10, marginBottom: 8, cursor: "pointer",
                      border: `2px solid ${selectedPM === pm.id && cardMode === "existing" ? "#3C2371" : "#e5e7eb"}`,
                      background: selectedPM === pm.id && cardMode === "existing" ? "rgba(60,35,113,0.04)" : "#fff",
                      transition: "all 0.15s",
                    }}
                  >
                    <CardBrandIcon brand={pm.card?.brand} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {pm.card?.brand?.toUpperCase()} •••• {pm.card?.last4}
                      </div>
                      <div style={{ fontSize: 12, color: "#9ca3af" }}>
                        Expires {pm.card?.exp_month}/{pm.card?.exp_year}
                      </div>
                    </div>
                    {selectedPM === pm.id && cardMode === "existing" && (
                      <IoCheckmarkCircleOutline size={20} color="#3C2371" />
                    )}
                  </div>
                ))}
                {/* "Use a new card" toggle — only show when not already in new-card mode */}
                {cardMode !== "new" && (
                  <div
                    onClick={() => setCardMode("new")}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 14px", borderRadius: 10, cursor: "pointer", marginBottom: 4,
                      border: "2px dashed #e5e7eb",
                      color: "#6b7280", fontWeight: 600, fontSize: 13,
                    }}
                  >
                    <IoAddCircleOutline size={18} /> Use a new card
                  </div>
                )}
              </>
            )}

            {/* New card input — shown when cardMode is "new" or no saved cards */}
            {(cardMode === "new" || paymentMethods.length === 0) && (
              <div style={{ marginBottom: 4 }}>
                {paymentMethods.length > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <label style={{ fontWeight: 600, fontSize: 13 }}>New card</label>
                    <button
                      onClick={() => { setCardMode("existing"); setSelectedPM(paymentMethods[0]?.id); }}
                      style={{
                        background: "#f3f4f6", border: "none", borderRadius: "50%",
                        width: 22, height: 22, display: "flex", alignItems: "center",
                        justifyContent: "center", cursor: "pointer", color: "#6b7280",
                        fontSize: 14, lineHeight: 1, padding: 0,
                      }}
                      title="Back to saved cards"
                    >
                      ✕
                    </button>
                  </div>
                )}
                {paymentMethods.length === 0 && (
                  <label style={{ fontWeight: 600, fontSize: 13, display: "block", marginBottom: 8 }}>Enter card details</label>
                )}
                <div
                  ref={cardRef}
                  style={{
                    padding: "12px 14px", border: "1px solid #d1d5db",
                    borderRadius: 10, background: "#fff", minHeight: 46,
                  }}
                />
              </div>
            )}

            {cardError && (
              <div style={{ marginTop: 12, color: "#ef4444", fontSize: 13, background: "#fef2f2", padding: "8px 12px", borderRadius: 8 }}>
                {cardError}
              </div>
            )}

            {/* Summary */}
            <div style={{ marginTop: 16, background: "#f9fafb", borderRadius: 10, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "#6b7280" }}>You'll be charged</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: "#3C2371" }}>${addAmount}.00</span>
            </div>
          </div>
        </Modal>

      </div>
    </div>
  );
}
